import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { LoggerService } from '@/services';
import { onboardWhatsappCloud, type OnboardWhatsappCloudResponse } from '@/services/kori.service';
import {
  CLOUD_ONBOARDING_ERRORS,
  CLOUD_ONBOARDING_MESSAGES,
  WHATSAPP_CLOUD_CONFIG_ID,
} from '@/constants/whatsapp-cloud';
import { WHATSAPP_CONSTANTS } from '@/constants';
import type { FacebookAuthResponse } from '@/types/whatsapp';
import { useWebsiteId } from './useWebsiteId';

export type CloudOnboardingStep =
  | 'idle'
  | 'signup'
  | 'connecting'
  | 'pin_required'
  | 'connected'
  | 'error';

interface SignupAssets {
  phoneNumberId?: string;
  wabaId?: string;
}

/**
 * Embedded Signup v4 de Meta para un número nuevo (sin Coexistence), y el alta
 * de la línea en kori. Meta devuelve el `code` (callback de FB.login) y los ids
 * (evento `WA_EMBEDDED_SIGNUP`) por caminos distintos y sin orden garantizado:
 * cuando están los dos se manda todo a kori, que tiene 30 s para canjear el code.
 */
export const useWhatsAppCloudOnboarding = () => {
  const websiteId = useWebsiteId();
  const [step, setStep] = useState<CloudOnboardingStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardWhatsappCloudResponse | null>(null);

  const codeRef = useRef<string | null>(null);
  const assetsRef = useRef<SignupAssets | null>(null);
  const pinRef = useRef<string | undefined>(undefined);
  const submittedRef = useRef(false);

  const fail = useCallback((reason: string) => {
    setError(CLOUD_ONBOARDING_MESSAGES[reason] ?? CLOUD_ONBOARDING_MESSAGES.unknown);
    setStep('error');
  }, []);

  const submitIfReady = useCallback(async () => {
    const code = codeRef.current;
    const assets = assetsRef.current;
    if (!code || !assets || submittedRef.current) {
      return;
    }
    if (!assets.phoneNumberId || !assets.wabaId) {
      fail('missing_phone_number');
      return;
    }

    submittedRef.current = true;
    setStep('connecting');
    try {
      const response = await onboardWhatsappCloud(websiteId, {
        code,
        wabaId: assets.wabaId,
        phoneNumberId: assets.phoneNumberId,
        pin: pinRef.current,
      });
      LoggerService.info('Línea Cloud API conectada', {
        phoneNumberId: response.phoneNumberId,
      });
      setResult(response);
      setStep('connected');
    } catch (err) {
      const reason = isAxiosError(err) ? err.response?.data?.reason : undefined;
      LoggerService.error('Falló el onboarding de Cloud API', { reason });
      if (reason === CLOUD_ONBOARDING_ERRORS.TWO_STEP_PIN_REQUIRED) {
        setStep('pin_required');
        return;
      }
      fail(reason ?? 'unknown');
    }
  }, [websiteId, fail]);

  // Los ids llegan por postMessage desde la ventana de Meta.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!WHATSAPP_CONSTANTS.TRUSTED_ORIGINS.some((origin) => event.origin === origin)) {
        return;
      }
      let data: { type?: string; event?: string; data?: Record<string, string> };
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (data.type !== 'WA_EMBEDDED_SIGNUP') {
        return;
      }

      if (data.event?.startsWith('FINISH')) {
        assetsRef.current = {
          phoneNumberId: data.data?.phone_number_id,
          wabaId: data.data?.waba_id,
        };
        void submitIfReady();
      } else if (data.event === 'CANCEL') {
        LoggerService.warn('Embedded Signup cancelado', { step: data.data?.current_step });
        setStep('idle');
      } else if (data.event === 'ERROR') {
        LoggerService.error('Embedded Signup reportó un error', data.data);
        fail(CLOUD_ONBOARDING_ERRORS.META_ERROR);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [submitIfReady, fail]);

  const launch = useCallback(
    (pin?: string) => {
      if (typeof window.FB === 'undefined') {
        fail('unknown');
        return;
      }
      if (!WHATSAPP_CLOUD_CONFIG_ID) {
        LoggerService.error('Falta FB_CLOUD_CONFIG_ID: no se puede lanzar el signup de Cloud API');
        fail('unknown');
        return;
      }

      codeRef.current = null;
      assetsRef.current = null;
      submittedRef.current = false;
      pinRef.current = pin;
      setError(null);
      setStep('signup');

      // v4: el flujo lo define la configuración de Facebook Login for Business,
      // `extras` va vacío a propósito.
      try {
        window.FB.login(
          (response: FacebookAuthResponse) => {
            const code = response.authResponse?.code;
            if (!code) {
              // Cerró la ventana sin terminar.
              setStep((current) => (current === 'signup' ? 'idle' : current));
              return;
            }
            codeRef.current = code;
            void submitIfReady();
          },
          {
            config_id: WHATSAPP_CLOUD_CONFIG_ID,
            response_type: 'code',
            override_default_response_type: true,
            extras: {},
          },
        );
      } catch (err) {
        LoggerService.error('FB.login no pudo lanzar el signup de Cloud API', {
          message: (err as Error).message,
        });
        fail('unknown');
      }
    },
    [submitIfReady, fail],
  );

  return { step, error, result, launch };
};
